// Request-scheduler voor de mount-burst (perf 2026-08-11).
//
// Probleem: bij mount vuurde het dashboard ~15 requests tegelijk af. Alle
// API-handlers op de Pi zijn synchroon en vechten om de GIL, dus een brede
// fan-out maakt de kritieke calls aantoonbaar tráger: /api/history/24h kost
// 1,8-2,2 s alleen, maar 6,7 s in de fan-out.
//
// Oplossing: elke data-bron laat zijn EERSTE fetch via deze scheduler lopen.
// De scheduler laat er maximaal `MAX_CONCURRENT` tegelijk door en geeft de
// laagste `priority` voorrang. Vervolg-polls gaan er NIET doorheen — de
// polling-intervallen en dus de versheid van de data blijven ongewijzigd.
//
// Bewust generiek: geen site-specifieke aannames, alleen een volgorde die
// zegt welke data de gebruiker het eerst nodig heeft.

/** Hoeveel initiële requests tegelijk mogen lopen. */
const MAX_CONCURRENT = 3;

/**
 * Vangnet: een taak die nooit settelt (hangende fetch, netwerk-blackhole) mag
 * geen slot permanent vasthouden. Na deze tijd geven we het slot vrij; de
 * onderliggende promise loopt gewoon door naar zijn eigen caller.
 */
const SLOT_WATCHDOG_MS = 20_000;

/**
 * Volgorde van de initiële fetches. Lager = eerder. De nummers zijn ruim
 * uit elkaar gezet zodat er later tussengevoegd kan worden zonder alles te
 * hernummeren.
 */
export const RequestPriority = {
  /** Hero-row + FlowSVG kunnen niets zonder snapshot. */
  snapshot: 0,
  /** Prijzen sturen de grafiek én de adaptieve horizon. */
  prices: 10,
  /** Historie vult de linkerhelft van de grafiek. */
  history: 20,
  socForecast: 30,
  pvForecast: 40,
  health: 50,
  installation: 60,
  savings: 70,
  /** Controls-kolom: tegel-state, mag na de hoofdgrafiek. */
  controls: 80,
  /** Diagnostiek (Tech-pagina) en overige secundaire bronnen. */
  secondary: 90,
} as const;

const DEFAULT_PRIORITY: number = RequestPriority.secondary;

interface QueueItem {
  priority: number;
  /** Tie-breaker zodat gelijke prioriteiten FIFO blijven. */
  seq: number;
  start: () => void;
}

let active = 0;
let seqCounter = 0;
let pumpGepland = false;
const queue: QueueItem[] = [];

function pump(): void {
  while (active < MAX_CONCURRENT && queue.length > 0) {
    queue.sort((a, b) => a.priority - b.priority || a.seq - b.seq);
    const item = queue.shift() as QueueItem;
    active += 1;
    item.start();
  }
}

/**
 * Deel slots pas uit nadat het huidige synchrone blok klaar is.
 *
 * Zonder dit uitstel vertrekt elke aanmelder meteen zolang er nog een slot vrij
 * is, en dan is de wachtrij bij het uitdelen precies één item lang: de
 * sortering komt nooit aan bod en de eerste drie gaan in aankomstvolgorde. Bij
 * het mounten melden de tegels in de bedieningskolom zich eerder aan dan de
 * snapshot, dus juist de data waar het stroomdiagram op wacht bleef achteraan
 * hangen. Gemeten tegen een live Pi: de snapshot startte 617 ms later dan
 * zonder planner, wat de planner van een verbetering in een verslechtering
 * veranderde.
 *
 * Een microtask-grens is genoeg. React voert de effecten van één commit
 * synchroon uit, dus alle aanmeldingen uit die commit staan in de wachtrij
 * voordat deze draait, en de vertraging zelf is niet meetbaar.
 *
 * Bij het vrijkomen van een slot pompen we wél direct: dan staat de rest al in
 * de wachtrij en is er niets te verzamelen.
 */
function planPump(): void {
  if (pumpGepland) return;
  pumpGepland = true;
  queueMicrotask(() => {
    pumpGepland = false;
    pump();
  });
}

/**
 * Voer `task` uit zodra er een slot vrij is. Het resultaat (of de fout) van
 * de taak wordt onveranderd doorgegeven — de aanroeper merkt alleen dat de
 * start een moment later kan vallen.
 */
export function scheduleRequest<T>(
  task: () => Promise<T>,
  priority: number = DEFAULT_PRIORITY,
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const start = () => {
      let released = false;
      let watchdog: ReturnType<typeof setTimeout>;
      const release = () => {
        if (released) return;
        released = true;
        clearTimeout(watchdog);
        active -= 1;
        pump();
      };
      watchdog = setTimeout(release, SLOT_WATCHDOG_MS);
      let running: Promise<T>;
      try {
        running = task();
      } catch (err) {
        release();
        reject(err);
        return;
      }
      running.then(
        (value) => { release(); resolve(value); },
        (err) => { release(); reject(err); },
      );
    };
    queue.push({ priority, seq: seqCounter++, start });
    planPump();
  });
}

/** Alleen voor tests: gooi de wachtrij weg en zet de teller terug. */
export function __resetRequestScheduler(): void {
  queue.length = 0;
  active = 0;
  seqCounter = 0;
  pumpGepland = false;
}
